import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wallet, Users, CalendarDays, Building2, Bus, MapPin, Sun, Sparkles,
  ArrowRight, ShieldCheck, GraduationCap, Globe2, Handshake, Compass, Leaf,
} from "lucide-react";
import api from "@/lib/api";
import PackageCard from "@/components/PackageCard";
import MapleLeaf from "@/components/MapleLeaf";

const DEST_OPTS = [
  { id: "", label: "All destinations" },
  { id: "canada", label: "Canada" },
  { id: "malaysia", label: "Malaysia" },
  { id: "indonesia", label: "Indonesia" },
  { id: "india", label: "India" },
];

const DUR_OPTS = [
  { id: "", label: "Any duration" },
  { id: "10", label: "10 Days" },
  { id: "20", label: "20 Days" },
  { id: "30", label: "30 Days" },
];

const INVESTMENT_FACTORS = [
  { icon: Users, label: "Group size" },
  { icon: CalendarDays, label: "Program duration" },
  { icon: Building2, label: "Accommodation category" },
  { icon: MapPin, label: "Destination city" },
  { icon: GraduationCap, label: "Educational activities" },
  { icon: Bus, label: "Transportation requirements" },
  { icon: Sun, label: "Season" },
  { icon: Sparkles, label: "Custom inclusions" },
];

const CUSTOM_FACTORS = [
  "Number of participants",
  "Budget",
  "Learning objectives",
  "Preferred cities",
  "Industry interests",
  "Travel dates",
  "Duration",
];

const TRUST_ITEMS = [
  { icon: MapleLeaf, label: "Canadian-operated programs" },
  { icon: ShieldCheck, label: "Safe and supervised learning environment" },
  { icon: Building2, label: "Industry and university exposure" },
  { icon: Globe2, label: "Global networking opportunities" },
  { icon: Compass, label: "Flexible customized itineraries" },
  { icon: Handshake, label: "Dedicated student support" },
];

export default function Packages() {
  const [dest, setDest] = useState("");
  const [dur, setDur] = useState("");
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (dest) params.set("destination", dest);
    if (dur) params.set("duration_days", dur);
    return params.toString();
  }, [dest, dur]);

  useEffect(() => {
    setLoading(true);
    api.get(`/packages${query ? `?${query}` : ""}`)
      .then(({ data }) => setPackages(data))
      .finally(() => setLoading(false));
  }, [query]);

  let grid;
  if (loading) {
    grid = <p className="text-slate-500">Loading…</p>;
  } else if (packages.length === 0) {
    grid = <p className="text-slate-500">No packages match those filters.</p>;
  } else {
    grid = (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="packages-grid">
        {packages.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} />)}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16" data-testid="packages-page">
      <div className="max-w-3xl mb-10">
        <p className="text-sm font-bold text-[#D52B1E] uppercase tracking-widest mb-3 inline-flex items-center gap-2">
          <MapleLeaf className="w-4 h-4" /> Packages
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05]">
          Customized International{" "}
          <span className="gradient-maple-text">Learning Packages</span>
        </h1>
        <p className="mt-5 text-slate-600">
          Every StudyTrip Global package is fully tailored to your group's size, budget, learning objectives, and travel dates.
        </p>
      </div>

      {/* Estimated Investment Banner */}
      <section
        className="relative overflow-hidden rounded-[28px] mb-14 border border-red-100 bg-gradient-to-br from-white via-[#FFF5F3] to-[#FAF6EE] shadow-xl shadow-red-500/10"
        data-testid="estimated-investment-banner"
      >
        <MapleLeaf className="absolute -top-6 -right-6 w-40 h-40 text-red-100" />
        <MapleLeaf className="absolute bottom-6 right-10 w-16 h-16 text-red-100/70 rotate-12" />
        <div className="relative p-8 md:p-10 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-red-100 shadow-sm mb-4">
              <Wallet className="w-4 h-4 text-[#D52B1E]" />
              <span className="text-[11px] font-bold text-slate-700 uppercase tracking-widest">Estimated Investment</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight">
              Starting from approximately
            </h2>
            <div className="mt-4 inline-flex items-baseline gap-3">
              <span
                className="text-4xl md:text-5xl font-extrabold gradient-maple-text tabular-nums"
                style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
              >
                USD 300 – 500
              </span>
              <span className="text-sm md:text-base font-semibold text-slate-700">per person / per night*</span>
            </div>
            <p className="mt-5 text-sm text-slate-600 leading-relaxed max-w-xl">
              Use this as a planning guide only. Final pricing is prepared for each group after a short discovery call.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="investment-request-quote"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full gradient-maple text-white font-semibold text-sm shadow-lg shadow-red-500/30 hover:shadow-xl hover:-translate-y-0.5 transition-transform"
              >
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/partner"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-200 hover:border-slate-300 text-slate-900 font-semibold text-sm transition-colors"
              >
                Partner With Us
              </Link>
            </div>
          </div>

          <div className="lg:col-span-2">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-3">Estimated pricing may vary depending on</p>
            <ul className="grid grid-cols-2 gap-2">
              {INVESTMENT_FACTORS.map((f) => (
                <li key={f.label} className="flex items-center gap-2 text-sm text-slate-700">
                  <span className="w-7 h-7 rounded-lg bg-white border border-slate-200 grid place-items-center shrink-0">
                    <f.icon className="w-3.5 h-3.5 text-[#D52B1E]" />
                  </span>
                  {f.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="relative bg-white/70 border-t border-red-100 px-8 md:px-10 py-4 text-xs text-slate-600 italic">
          *Estimated only. International airfare, visa fees, travel insurance, personal expenses, and optional activities are not included unless specifically stated.
        </div>
      </section>

      {/* Filters */}
      <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-4 md:p-5 mb-10 grid gap-3 md:grid-cols-2" data-testid="packages-filters">
        <select value={dest} onChange={(e) => setDest(e.target.value)} data-testid="packages-filter-destination" className="px-3 py-2.5 rounded-xl bg-slate-50 border border-transparent focus:border-[#D52B1E] focus:bg-white text-sm outline-none">
          {DEST_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        <select value={dur} onChange={(e) => setDur(e.target.value)} data-testid="packages-filter-duration" className="px-3 py-2.5 rounded-xl bg-slate-50 border border-transparent focus:border-[#D52B1E] focus:bg-white text-sm outline-none">
          {DUR_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </div>

      {grid}

      {/* Custom Programs section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="mt-20 rounded-[28px] overflow-hidden bg-gradient-to-br from-[#E4F1EE] via-white to-[#E1F1F3] border border-emerald-100 p-8 md:p-12 relative"
        data-testid="custom-programs-section"
      >
        <Leaf className="absolute -top-8 -right-8 w-40 h-40 text-emerald-100 rotate-12" />
        <div className="relative grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="text-sm font-bold text-[#1F4A3A] uppercase tracking-widest mb-3">Need Something Different?</p>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight">
              Every program is fully <span className="text-[#1F4A3A]">customizable</span>
            </h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Every StudyTrip Global program is fully customizable for schools, colleges, universities, training institutes, corporate groups and educational organizations.
            </p>
            <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5 inline-flex items-center gap-4 shadow-sm">
              <div className="w-11 h-11 rounded-xl bg-[#1F4A3A] text-white grid place-items-center">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Minimum recommended group size</p>
                <p className="font-display text-2xl font-extrabold text-slate-900 tabular-nums" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>20 Participants</p>
              </div>
            </div>
          </div>
          <div>
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-3">We design programs based on</p>
            <ul className="grid grid-cols-2 gap-2">
              {CUSTOM_FACTORS.map((f) => (
                <li key={f} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-emerald-100 text-sm text-slate-800">
                  <Sparkles className="w-3.5 h-3.5 text-[#1F4A3A]" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.section>

      {/* Big CTA banner */}
      <section className="mt-14 relative overflow-hidden rounded-[36px] gradient-maple text-white p-10 md:p-16" data-testid="packages-cta-banner">
        <div className="absolute -top-10 -right-10 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-96 h-96 bg-amber-300/25 rounded-full blur-3xl" />
        <MapleLeaf className="absolute top-8 right-8 w-16 h-16 text-white/10" />
        <div className="relative max-w-2xl">
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/85 mb-4">
            <MapleLeaf className="w-3.5 h-3.5" /> Your Global Classroom
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
            Ready to Experience Canada?
          </h2>
          <p className="mt-4 text-white/90 text-lg max-w-xl">
            Bring your students, colleagues or institution on a fully-tailored StudyTrip Global program.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/apply"
              data-testid="packages-cta-request-program"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#D52B1E] font-semibold text-sm hover:bg-slate-100 transition-colors shadow-lg"
            >
              Request Customized Program <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/partner"
              data-testid="packages-cta-partner"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold text-sm transition-colors"
            >
              Partner With Us
            </Link>
            <Link
              to="/contact"
              data-testid="packages-cta-contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold text-sm transition-colors"
            >
              Contact Our Team
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Message */}
      <section className="mt-14 rounded-3xl bg-white border border-slate-100 shadow-sm p-8 md:p-10" data-testid="packages-trust-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-sm font-bold text-[#D52B1E] uppercase tracking-widest mb-2">Why Choose StudyTrip Global?</p>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 leading-tight">
              A trusted partner for global learning
            </h2>
          </div>
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {TRUST_ITEMS.map((t) => (
            <li key={t.label} className="flex items-center gap-3 p-4 rounded-2xl bg-[#FAF6EE] border border-amber-100">
              <span className="w-9 h-9 rounded-xl bg-white border border-red-100 grid place-items-center shrink-0">
                <t.icon className="w-4 h-4 text-[#D52B1E]" />
              </span>
              <span className="text-sm font-semibold text-slate-800">{t.label}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
